import { ColDef, RowClassParams } from "ag-grid-community";
import { dashOrEuDateFormatter, dashOrValueFormatter } from "@/lib/grid/formatters";
import { labelledSetFilter, utcDateFilterParams } from "@/utils/grid";
import { Apartment } from "@/models/responses";
import { ApartmentImageRenderer, ApartmentNameLinkRenderer } from "./apartments.renderers";
import type { ApartmentRow } from "./apartments";

// Pure grid-row helpers extracted from apartments.tsx so that page file exports
// only its component(s) — a mixed component + plain-function export breaks React
// Fast Refresh (react-refresh/only-export-components).

/**
 * An apartment is decommissioned (as of `today`) iff it carries a removal date
 * that has been reached: `dateRemoved != null && dateRemoved <= today`. A
 * future-dated removal is still active (AS-7), so it is NOT decommissioned.
 *
 * This is the dual of `isActiveNow` in `useApartmentFilters`, but expressed on
 * the grid row (which carries `dateRemoved: Date | null`) so the list's row
 * styling and the name-cell badge share one rule.
 */
export function isDecommissioned(row: Pick<ApartmentRow, "dateRemoved">, today: Date): boolean {
    return row.dateRemoved != null && row.dateRemoved <= today;
}

/**
 * Per-row CSS for the AG Grid `getRowClass` hook: decommissioned rows render
 * muted (`text-muted-foreground` + subtle `bg-gray-50`). Muting is a *secondary*
 * cue — the `Decommissioned` text badge in the name cell is the primary,
 * non-colour signal (WCAG 1.4.1).
 */
export function decommissionedRowClass(params: Pick<RowClassParams<ApartmentRow>, "data">, today: Date): string | undefined {
    if (params.data && isDecommissioned(params.data, today)) {
        return "text-muted-foreground bg-gray-50";
    }
    return undefined;
}

export function buildApartmentColumnDefinitions(): ColDef<ApartmentRow>[] {
    return [
        {
            field: "imageUrl",
            headerName: "Image",
            width: 150,
            cellDataType: "",
            filter: false,
            cellRenderer: ApartmentImageRenderer,
        },
        {
            field: "name",
            headerName: "Name",
            width: 200,
            cellDataType: "",
            filter: "agTextColumnFilter",
            wrapText: true,
            cellRenderer: ApartmentNameLinkRenderer,
        },
        {
            field: "shortName",
            headerName: "Short Name",
            width: 150,
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
        {
            field: "liveSince",
            headerName: "Live Since",
            width: 180,
            cellDataType: "date",
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            valueFormatter: dashOrEuDateFormatter<ApartmentRow>,
        },
        {
            field: "dateRemoved",
            headerName: "Date Removed",
            width: 180,
            cellDataType: "date",
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            valueFormatter: dashOrEuDateFormatter<ApartmentRow>,
        },
        {
            field: "owners",
            headerName: "Owners",
            width: 180,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            wrapText: true,
        },
        {
            field: "group",
            headerName: "Group",
            width: 100,
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
        {
            field: "floor",
            headerName: "Floor",
            width: 100,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
        },
        {
            field: "nBeds",
            headerName: "Bedrooms",
            width: 100,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
        },
        {
            field: "squareMeters",
            headerName: "Square Meters",
            width: 120,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
        },
        {
            field: "shortTermRegistryId",
            headerName: "AMA",
            width: 100,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "energyCertificate",
            headerName: "Energy Certificate",
            width: 130,
            cellDataType: "date",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrEuDateFormatter<ApartmentRow>,
        },
        {
            field: "initialTaxisRentalAgreement",
            headerName: "Initial Taxis",
            width: 100,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "latestTaxisRentalAgreement",
            headerName: "Latest Taxis",
            width: 100,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "initialTaxisRentalAgreement",
            headerName: "Rental Contract Taxis",
            width: 140,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "internetCompany",
            headerName: "Internet",
            width: 120,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "internetLine",
            headerName: "Internet Line",
            width: 120,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "internetUsername",
            headerName: "Internet Username",
            width: 130,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "internetPassword",
            headerName: "Internet Password",
            width: 130,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "atak",
            headerName: "ATAK",
            width: 100,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "electricityCompany",
            headerName: "Electricity Company",
            width: 130,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "electricityCertificateTerminationDate",
            headerName: "Electricity Certificate Termination Date",
            width: 200,
            cellDataType: "date",
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            valueFormatter: dashOrEuDateFormatter<ApartmentRow>,
        },
        {
            field: "electricityRf",
            headerName: "Electricity RF",
            width: 130,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "electricitySupplyId",
            headerName: "Electricity Supply ID",
            width: 130,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "eydapSupplyId",
            headerName: "EYDAP Supply ID",
            width: 130,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "eydapRegistryId",
            headerName: "EYDAP Registry ID",
            width: 140,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        // address-normalization/FR-029: the address column carries the
        // building's canonical string, so its Set Filter must offer those
        // strings — never the internal address reference. `labelledSetFilter`
        // formats the filter list itself (the Set Filter never reads
        // `cellRenderer`), with blanks shown as the same dash the cell renders.
        {
            field: "address",
            headerName: "Address",
            width: 200,
            cellDataType: "text",
            ...labelledSetFilter((value) => (value == null || value === "" ? "-" : String(value))),
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "disinfectionDate",
            headerName: "Disinfection Date",
            width: 150,
            cellDataType: "date",
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            valueFormatter: dashOrEuDateFormatter<ApartmentRow>,
        },
        {
            field: "disinfectionCertificateNumber",
            headerName: "Disinfection Certificate",
            width: 140,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "fireExtinguisherRefillCertificateDate",
            headerName: "Fire Extinguisher Refill Certificate Date",
            width: 200,
            cellDataType: "date",
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            valueFormatter: dashOrEuDateFormatter<ApartmentRow>,
        },
        {
            field: "insuranceCompany",
            headerName: "Insurance Company",
            width: 140,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "insuranceContractId",
            headerName: "Insurance Contract ID",
            width: 140,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "airbnbUrl",
            headerName: "Airbnb URL",
            width: 120,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
        {
            field: "bookingUrl",
            headerName: "Booking URL",
            width: 120,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            valueFormatter: dashOrValueFormatter<ApartmentRow>,
        },
    ];
}

/**
 * Pure apartment → grid-row mapping. Resolver callbacks (`ownerName`,
 * `groupName`) are injected so the function stays free of the `useAppData`
 * hook and is directly unit-testable. The crucial addition for this feature is
 * `dateRemoved` (null when active) — it feeds the Date Removed column, the muted
 * row class, and the name-cell badge.
 */
export function mapApartmentToRow(
    apartment: Apartment,
    ownerName: (ownerId: number) => string,
    groupName: (groupId: number) => string,
): ApartmentRow {
    return {
        id: apartment.id,
        name: apartment.name,
        shortName: apartment.shortName,
        liveSince: apartment.dateAdded,
        dateRemoved: apartment.dateRemoved ? new Date(apartment.dateRemoved) : null,
        owners: apartment.ownerIds.map((ownerId) => ownerName(ownerId)),
        group: groupName(apartment.groupId),
        nBeds: apartment.numberOfBeds ?? 0,
        squareMeters: apartment.squareMeters ?? 0,
        energyCertificate: apartment.energyCertificateIssuanceDate ? new Date(apartment.energyCertificateIssuanceDate) : null,
        initialTaxisRentalAgreement: apartment.initialTaxisRentalAgreement ?? "",
        latestTaxisRentalAgreement: apartment.latestRentalTaxisAgreement ?? "",
        internetCompany: apartment.internetCompany ?? "",
        internetLine: apartment.internetLine ?? "",
        internetUsername: apartment.internetUsername ?? "",
        internetPassword: apartment.internetPassword ?? "",
        atak: apartment.atak ?? "",
        shortTermRegistryId: apartment.shortTermRegistryId ?? "",
        electricityCompany: apartment.electricityCompany ?? "",
        electricityRf: apartment.electricityRf ?? "",
        electricitySupplyId: apartment.electricitySupplyId ?? "",
        electricityCertificateTerminationDate: apartment.electricityCertificateTerminationDate
            ? new Date(apartment.electricityCertificateTerminationDate)
            : null,
        eydapSupplyId: apartment.eydapSupplyId ?? "",
        eydapRegistryId: apartment.eydapRegistryId ?? "",
        address: apartment.address ?? "",
        imageUrl: apartment.imageUrl || "https://reviveyouthandfamily.org/wp-content/uploads/2016/11/house-placeholder.jpg",
        disinfectionDate: apartment.disinfectionDate ? new Date(apartment.disinfectionDate) : null,
        disinfectionCertificateNumber: apartment.disinfectionCertificateNumber ?? "",
        fireExtinguisherRefillCertificateDate: apartment.fireExtinguisherRefillCertificateDate
            ? new Date(apartment.fireExtinguisherRefillCertificateDate)
            : null,
        floor: apartment.floor ?? 0,
        insuranceCompany: apartment.insuranceCompany ?? "",
        insuranceContractId: apartment.insuranceContractId ?? "",
        airbnbUrl: apartment.airbnbUrl ?? "",
        bookingUrl: apartment.bookingUrl ?? "",
    };
}
